import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Quest, QuestCheckResult, QuestValueChange } from '@vi/quest';
import { Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { ConfigurationResultService } from '../../services/configuration-result.service';
import { PermissionService } from '../../services/permission.service';
import { ProductsCategoryService } from '../../services/products-category.service';
import { LineType, Quote, QuoteService } from '../../services/quote.service';
import { SelectedCustomerService } from '../../services/selected-customer.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { CustomerSelection } from '../customer-selection/customer-selection.component';
import { LoadingOverlayComponent } from '../loading-overlay/loading-overlay.component';
import { PriceSummaryDataService } from '../price-summary/price-summary.data.service';
import { flatProductList, ProductListService } from '../product-list/product-list.service';
import {
    ConfigitAssignment,
    ConfigitConfiguration,
    ConfigitConfigurationAndProducts,
} from './configit-quest-adapter/configit-types.interface';
import { PackerConfigitQuestAdapterService } from './configit-quest-adapter/packer-configit-quest-adapter.service';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { environment } from '../../../../environments/environment';
import { QuestPartReset } from '@vi/quest/lib/quest-part-reset.interface';
import { openConfirmationDialog } from '../confirmation-dialog/confirmation-dialog.component';
import { DropoutDialogComponent, DropoutDialogData } from '../dropout-dialog/dropout-dialog.component';
import { InstanaService } from '../../services/instana.service';
import { CustomRouteReuseStrategy } from '../../utils/route-reuse.strategy';
import { AppService } from '../../services/app.service';
import { SampleOfferNotFoundError } from '../../services/offer.service';
import { ConfigitEngine, QuoteAssignment, QuoteStatus } from '../../models/quote.model';
import { AceQuestAdapterService } from './configit-ace-quest-adapter/ace-quest-adapter.service';
import { fromConfigitAssignments, userAssignments } from '../../utils/configit-ace.helper';
import { ConfigitAceConfiguration } from '../../models/configit-ace.model';
import { Product } from '../../services/products.service';

@Component({
    selector: 'app-questionnaire',
    templateUrl: './questionnaire.component.html',
    styleUrls: ['./questionnaire.component.scss'],
})
export class QuestionnaireComponent implements OnInit, OnDestroy {
    private ngUnsubscribe: Subject<void> = new Subject<void>();
    private dialogRef: MatDialogRef<LoadingOverlayComponent>;
    private selectedCustomer: CustomerSelection = { name: '', number: '' };
    private isPriceSummaryLoading = false;
    private hasPriceSummaryError = false;

    private showInitialModel: boolean;
    private hybridModel: {
        first: string;
        second: string;
    };

    public displayWaitMessage = false;
    public displaySelectCustomerMsg = false;
    public displayProvideTitleMsg = false;
    public displayMissingMainComponentMsg = false;
    public displayPriceSummaryError = false;

    public preselectPart = 0;

    public quest$ = new Subject<Quest>();
    public currentModel: Quest;

    @Input()
    public quoteId: string;
    @Input()
    public title: string;
    @Input()
    public salesforceId: string;
    @Input()
    public quotationRequestId: string;
    @Input()
    public planning: boolean;
    @Input()
    public lineType: LineType;

    @Output()
    public isReady = new EventEmitter<boolean>();

    @Output()
    public isComplete = new EventEmitter<boolean>();

    constructor(
        private quests: PackerConfigitQuestAdapterService,
        private ace: AceQuestAdapterService,
        private appService: AppService,
        private router: Router,
        private quoteService: QuoteService,
        private selectedCustomerService: SelectedCustomerService,
        private snackBarService: SnackBarService,
        private translateService: TranslateService,
        private instana: InstanaService,
        private productListService: ProductListService,
        private dialog: MatDialog,
        private permissionService: PermissionService,
        private priceSummaryDataService: PriceSummaryDataService,
        private productCategoryService: ProductsCategoryService,
        private configurationResultService: ConfigurationResultService
    ) {}

    public ngOnInit() {
        this.configurationResultService.configuration$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
            error: (error) => {
                if (error instanceof SampleOfferNotFoundError) {
                    this.onError('CONFIGURATION.SNACK_BAR.SAMPLE_OFFER.NOT_FOUND.ERROR');
                } else {
                    this.onError('CONFIGURATION.SNACK_BAR.UPDATE_VALUES.ERROR');
                }
            },
        });
        this.productCategoryService.clearCategoriesCache();
        this.loadQuote();
        this.subscribeToSelectedCustomer$();
        this.subscribeToPriceSummary();
        this.subscribeToQuest();
    }

    public page(): void {}

    public reset(event: QuestPartReset) {
        const title = this.translateService.instant('CONFIGURATION.DIALOG.RESET.TITLE');
        const question = this.translateService.instant('CONFIGURATION.DIALOG.RESET.MESSAGE', {
            page: event.part.title,
        });
        const yes = this.translateService.instant('CONFIGURATION.DIALOG.RESET.CONFIRM');
        const no = this.translateService.instant('CONFIGURATION.DIALOG.RESET.CANCEL');

        openConfirmationDialog(this.dialog, title, question, yes, no, (confirmed) => {
            if (confirmed) {
                this.openDialog();
                this.productCategoryService.clearCategoriesCache();

                if (this.appService.isAceModel(event.quest.id)) {
                    this.ace
                        .reset(event, this.quoteId)
                        .pipe(takeUntil(this.ngUnsubscribe))
                        .subscribe((model: Quest) => {
                            this.setModel(model);

                            const config: ConfigitAceConfiguration = model.original;

                            this.quoteService.updateAceAssignments(
                                config.configuration.configurationAssignments,
                                config.product.id
                            );

                            this.closeDialog();
                        });
                } else {
                    this.quests
                        .reset(event)
                        .pipe(takeUntil(this.ngUnsubscribe))
                        .subscribe((model: Quest) => {
                            this.setModel(model);
                            this.quoteService.updateModelAssignments(
                                model.original.assignments,
                                model.original.template.name
                            );
                            this.closeDialog();
                        });
                }
            } else {
                // set current model as otherwise quest will stay in "checking" state
                this.setModel(this.currentModel);
            }
        });
    }

    public check(changed: QuestValueChange) {
        this.configurationResultService.isConfigurationUpdating$.next(true);
        if (this.appService.isAceModel(changed.quest.id)) {
            this.ace.check(changed, this.quoteId).subscribe({
                next: (model: Quest) => {
                    this.setModel(model);

                    const config: ConfigitAceConfiguration = model.original;

                    this.quoteService.updateAceAssignments(
                        config.configuration.configurationAssignments,
                        config.product.id
                    );
                },
                error: () => {
                    this.onError('CONFIGURATION.SNACK_BAR.UPDATE_VALUES.ERROR');
                },
            });
        } else {
            this.quests
                .check(changed)
                .pipe(map((result: QuestCheckResult) => result.model))
                .subscribe(
                    (model: Quest) => {
                        this.setModel(model);

                        this.quoteService.updateModelAssignments(
                            model.original.assignments,
                            model.original.template.name
                        );
                        if (this.showInitialModel && this.checkComplete(model)) {
                            // switch to the secondary model as soon as the primary model is complete
                            this.switchToSecondaryModel(model);
                        }
                    },
                    () => {
                        this.onError('CONFIGURATION.SNACK_BAR.UPDATE_VALUES.ERROR');
                    }
                );
        }
    }

    private initializeModelLine(material: string, lineType: LineType, assignments: QuoteAssignment[]) {
        // copy all assignments from Miscellaneous.PreSelection to the new model as user assignment
        const copyAssignments: QuoteAssignment[] = assignments
            .filter((a) => a.variableName.startsWith(environment.quest.preselectionVariable))
            .map((a) => ({ ...a, isUserAssignment: true }));
        if (this.appService.isAceModel(material)) {
            const fromConfigit = fromConfigitAssignments(copyAssignments);
            return this.quoteService.createAdditionalLine(material, lineType, ConfigitEngine.ace, fromConfigit);
        }
        return this.quoteService.createAdditionalLine(material, lineType, ConfigitEngine.model, copyAssignments);
    }

    private switchToModel(quest: Quest, material: string, lineType: LineType) {
        this.openDialog();
        this.initializeModelLine(material, lineType, quest.original.assignments)
            .pipe(concatMap(() => this.productListService.validateMainComponent(quest.id)))
            .subscribe({
                next: (validateResult) => {
                    this.closeDialog();
                    if (validateResult.hasMainComponent) {
                        this.navigateToModel(lineType);
                    } else {
                        this.sendNoMainComponentNotification();
                        this.displayMissingMainComponentMsg = true;
                    }
                },
                error: (e) => {
                    console.error(e);
                    this.onError('CONFIGURATION.SNACK_BAR.SAVE_CONFIGURATION.ERROR');
                    this.closeDialog();
                },
            });
    }

    private navigateToModel(lineType: string) {
        const strategy = <CustomRouteReuseStrategy>this.router.routeReuseStrategy;
        strategy.canReuseRoute = false;
        this.router
            .navigate([], {
                queryParams: { lineType },
                queryParamsHandling: 'merge',
            })
            .then(() => {
                this.closeDialog();
                strategy.canReuseRoute = true;
            });
    }

    private switchToSecondaryModel(quest: Quest) {
        const material = quest.original.assignments
            .find((a) => a?.variableName === environment.quest.modelSelectionVariable)
            .valueName.split(';')[0];
        this.switchToModel(quest, material, LineType.secondary);
    }

    private switchToTertiaryModel(quest: Quest, material: string) {
        this.switchToModel(quest, material, LineType.tertiary);
    }

    public submit(submittedModel?: Quest | Promise<Quest>) {
        const model: Quest = submittedModel ? <Quest>submittedModel : this.currentModel;

        if (this.isFirstHybridModel(model)) {
            // skip validation so we can go to second hybrid model
            this.switchToTertiaryModel(model, this.hybridModel.second);
            return;
        }

        this.displayWaitMessage = false;
        // Display error if there is no text in the title input
        this.displayProvideTitleMsg = !this.title;

        // when user is an employee he must first select a customer that the quote will be assigned to
        if (!this.selectedCustomer.number && this.permissionService.isUserAnEmployee) {
            this.displaySelectCustomerMsg = true;
        }

        this.displayPriceSummaryError = this.hasPriceSummaryError;

        // Don't submit when errors are displayed
        if (this.displayProvideTitleMsg || this.displaySelectCustomerMsg || this.displayPriceSummaryError) {
            return;
        }

        // wait till products and price finishes loading
        if (
            this.productListService.isProductListLoading$.value ||
            this.isPriceSummaryLoading ||
            this.configurationResultService.isConfigurationUpdating$.value
        ) {
            this.displayWaitMessage = true;

            setTimeout(() => {
                this.submit(model);
            }, 2000);
            return;
        }

        this.openDialog();

        const productListReady$ = this.productListService.productList$.pipe(map((v) => v?.groups?.length > 0));

        productListReady$
            .pipe(
                filter(Boolean),
                switchMap(() => this.productListService.productList$),
                flatProductList(),
                take(1),
                concatMap((products) => this.getEnergyLabel(model, products)),
                switchMap((energyLabelHash) => {
                    if (energyLabelHash) {
                        return this.quoteService.updateEnergyLabelHash(energyLabelHash);
                    }
                    return of(null);
                }),
                concatMap(() => this.productListService.validateMainComponent(model.id))
            )
            .subscribe({
                next: (result) => {
                    this.closeDialog();

                    if (result.hasMainComponent) {
                        if (['0500', '5000'].includes(this.appService.getSalesOrg())) {
                            const priceSummary = this.priceSummaryDataService.getLastPriceSummary();
                            const accessoryItems = (priceSummary.grossPrices || []).filter((item) =>
                                result.accessoryProducts.includes(item.material)
                            );
                            const accessoryTotalGross = accessoryItems.reduce((sum, item) => sum + item.gross, 0);
                            const totalGross = priceSummary.prices.totalGross;
                            const percentage = (accessoryTotalGross * 100) / totalGross;
                            const lowPercentage = percentage && percentage < 3;
                            const hasPriceAdvantage = priceSummary.prices.totalAdvantage > 0;
                            if (lowPercentage && hasPriceAdvantage) {
                                this.sendAccessoryNotification({ percentage, accessoryTotalGross, totalGross });
                            }
                            this.productCategoryService.setDiscountsFrozen(lowPercentage, result.mainCategory);
                        }
                        this.navigateToSummaryPage();
                    } else {
                        this.sendNoMainComponentNotification();
                        this.displayMissingMainComponentMsg = true;
                    }
                },
                error: (e) => {
                    console.error(e);
                    this.onError('CONFIGURATION.SNACK_BAR.SAVE_CONFIGURATION.ERROR');
                    this.closeDialog();
                },
            });
    }

    private getEnergyLabel(model: Quest, products: Product[]) {
        const getEnergyLabel = this.appService.isAceModel(model.id)
            ? this.ace.getEnergyLabelFromConfiguration(model, products)
            : this.quests.getEnergyLabelFromConfiguration(model, products);
        return getEnergyLabel.pipe(catchError(() => of(null))); // ignore errors on energy label generation. the error is shown on the next page
    }

    private sendNoMainComponentNotification() {
        this.sendInstanaNotification('mainComponent', 'No main component for quote');
    }

    private sendAccessoryNotification(data: { percentage: number; accessoryTotalGross: number; totalGross: number }) {
        this.sendInstanaNotification('accessory', 'Accessory less than 3% for quote', data);
    }

    private sendInstanaNotification(
        customType: 'mainComponent' | 'accessory',
        message: string,
        data: Record<string, string | number> = {}
    ) {
        const quote = this.quoteService.getCurrentQuote();
        if (quote) {
            const { quickRef, customerPurchaseNumber: quoteTitle, id: documentId } = quote;
            this.instana.reportError(`${message} ${quickRef}`, {
                customType,
                documentId,
                quoteTitle,
                quickRef,
                ...data,
            });
        }
    }

    private checkComplete(model: Quest): boolean {
        const firstIncomplete = model.parts.findIndex((p) => !p.valid);
        return firstIncomplete === -1;
    }

    private isFirstHybridModel(model: Quest) {
        return this.hybridModel && model.id === this.hybridModel.first;
    }

    private subscribeToQuest() {
        this.quest$.asObservable().subscribe((model: Quest) => {
            this.currentModel = model;
            this.isComplete.next(this.checkComplete(model) && !this.isFirstHybridModel(model));
        });
    }

    protected setModel(model: Quest): void {
        this.quest$.next(model);
        if (this.showInitialModel) {
            return;
        }
        if (this.isFirstHybridModel(model)) {
            const lastPart = model.parts[model.parts.length - 1];
            // quest.button.submit-hybrid
            lastPart.submit = 'submit-hybrid';
        }
        if (this.hybridModel && model.id === this.hybridModel.second) {
            // enable "exit" button on first part, which will switch back to first of hybrid models
            model.parts[0].exit = true;
        }
    }

    private onError(translationKey: string) {
        this.snackBarService.openSnackBar({
            message: this.translateService.instant(translationKey),
            isFailure: true,
        });
    }

    private openDialog(): void {
        if (!this.dialogRef) {
            const dialogConfig = new MatDialogConfig();
            dialogConfig.disableClose = true;
            dialogConfig.width = '250px';
            dialogConfig.scrollStrategy = new NoopScrollStrategy();
            this.dialogRef = this.dialog.open(LoadingOverlayComponent, dialogConfig);
        }
    }

    private closeDialog(): void {
        if (this.dialogRef) {
            this.dialogRef.close();
            this.dialogRef = null;
        }
    }

    private subscribeToPriceSummary() {
        this.priceSummaryDataService.isPriceSummaryLoading$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((isLoading) => {
                this.isPriceSummaryLoading = isLoading.value;
            });
        this.priceSummaryDataService.hasErrors$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((hasErrors) => {
            this.hasPriceSummaryError = hasErrors?.value;
        });
    }

    private loadQuote() {
        this.quoteService
            .fetchQuote(this.quoteId)
            .pipe(concatMap((quote) => this.initializeQuote(quote)))
            .subscribe((quote) => this.setupQuest(quote));
    }

    private initializeQuote(quote: Quote) {
        const primary = this.quoteService.getPrimaryLine(quote);
        if (quote.isLegacy || primary.assignments?.length) {
            // quote is either a configit quote or a quotation quote that is already initialized
            return of(quote);
        }
        return this.quests.getConfiguration(environment.quest.project).pipe(
            concatMap(({ configuration }) => {
                this.quoteService.setModelLineAssignments(configuration.newAssignments, primary);

                const assignments = <ConfigitAssignment[]>primary.assignments;
                const modelSelection = assignments.find(
                    (a) => a.variableName === environment.quest.modelSelectionVariable
                )?.valueName;
                if (modelSelection && configuration.valid && configuration.complete) {
                    const materials = modelSelection.split(';');
                    return this.initializeModelLine(
                        materials[0],
                        LineType.secondary,
                        configuration.newAssignments
                    ).pipe(map(() => quote));
                }
                return of(quote);
            })
        );
    }

    private setupQuest(quote: Quote) {
        if (quote.status !== QuoteStatus.new) {
            this.router.navigate(['next-steps'], {
                queryParams: { id: this.quoteId, sapDocumentId: quote.offerNumber },
            });
        }
        if (this.salesforceId) {
            this.quoteService.updateSalesforceOpportunityId(this.salesforceId);
        }
        const primaryLine = this.quoteService.getPrimaryLine(quote);
        if (!primaryLine) {
            // this means that we have a quote with only external products.
            // we should go to the summary page
            this.navigateToSummaryPage();
        }
        const secondaryLine = this.quoteService.getSecondaryLine(quote);
        const assignment = (<ConfigitAssignment[]>primaryLine.assignments).find(
            (a) => a.variableName === environment.quest.modelSelectionVariable
        );
        if (assignment?.valueName?.includes(';')) {
            const [first, second] = assignment.valueName.split(';');
            this.hybridModel = { first, second };
        }
        this.showInitialModel = !secondaryLine;
        const quoteLine = this.lineType && this.quoteService.getLineByType(quote, this.lineType);
        const line = quoteLine || secondaryLine || primaryLine;
        const material = line?.material || environment.quest.project;

        // always go to first part
        this.preselectPart = 0;

        // check if configuration is supported when a planning id is set in the quote object
        // this only needs to be done for the first model (QuoteAssist).
        // if there are further lines, then the first model was already valid and complete.
        const checkSupport = !!quote.salesforcePlanningId && material === environment.quest.project;
        const obs =
            line.engine === ConfigitEngine.ace
                ? this.ace.getModel(material, this.quoteId, userAssignments(line.assignments))
                : this.quests.getWithConfiguration(
                      material,
                      <Observable<ConfigitConfigurationAndProducts>>(
                          this.configurationResultService.getConfigurationForQuoteLine(line, this.quoteId, checkSupport)
                      )
                  );
        obs.subscribe({
            next: (model) => {
                if (!model || !model.original) {
                    // if model is NULL skip all operations
                    return;
                }
                if ((<ConfigitConfiguration>model.original.configuration).unsupported) {
                    // planning usecase with unsupported configuration,
                    // show a dropout dialog
                    return this.showDropoutForUnsupported(quote.salesforceOpportunityId);
                }

                this.setModel(model);
                this.isReady.emit(true);
                this.closeDialog();
            },
            error: () => {
                this.onError('CONFIGURATION.SNACK_BAR.FETCH_CONFIGURATION.ERROR');
                this.closeDialog();
            },
        });
    }

    private showDropoutForUnsupported(salesforceId: string) {
        // planning usecase with unsupported configuration,
        // show a dropout dialog
        const data: DropoutDialogData = {
            title: this.translateService.instant('CONFIGURATION.DROPOUT.UNSUPPORTED.TITLE'),
            text: this.translateService.instant('CONFIGURATION.DROPOUT.UNSUPPORTED.TEXT'),
        };
        const dialogRef = this.dialog.open(DropoutDialogComponent, { data });
        dialogRef.afterClosed().subscribe(() => {
            if (salesforceId) {
                // this usecase can only happen for planning quotes, so we should always have salesforceId
                this.router.navigate(['planning-project', salesforceId]);
            } else {
                this.router.navigate(['overview']);
            }
        });
        this.isReady.emit(true);
    }

    private subscribeToSelectedCustomer$() {
        this.selectedCustomerService.selectedCustomer$
            .pipe(takeUntil(this.ngUnsubscribe), filter(Boolean))
            .subscribe((customer: CustomerSelection) => {
                this.selectedCustomer = customer;
                this.displaySelectCustomerMsg =
                    this.selectedCustomer.number === undefined && this.permissionService.isUserAnEmployee;
            });
    }

    private navigateToSummaryPage() {
        const salesforceId = this.salesforceId;
        const quotationRequestId = this.quotationRequestId;
        this.router.navigate(['summary'], {
            queryParams: {
                id: this.quoteId,
                ...(this.planning && { planning: true }),
                ...(salesforceId && { salesforceId }),
                ...(quotationRequestId && { quotationRequestId }),
            },
        });
    }

    public ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public backToSecondary() {
        this.openDialog();
        this.navigateToModel(LineType.secondary);
    }
}
